import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

export interface FieldProps {
  label: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** the input; it receives id, aria-describedby, aria-invalid */
  children: ReactElement<Record<string, unknown>> | ReactNode;
  inline?: boolean;
  className?: string;
}

/** Label + control + help + error (spec/dashboard.md section 2.2): the label is bound to the control, errors are announced. */
export function Field({ label, help, error, required, children, inline, className = '' }: FieldProps) {
  const id = useId();
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children.props as { id?: string }).id ?? id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })
    : children;
  const controlId = isValidElement(children) ? ((children.props as { id?: string }).id ?? id) : id;
  return (
    <div className={`field ${inline ? 'row' : ''} ${className}`}>
      <label className="field-label" htmlFor={controlId}>
        {label}
        {required && <span className="req" aria-hidden="true">*</span>}
      </label>
      {control}
      {help && (
        <div className="field-help" id={helpId}>
          {help}
        </div>
      )}
      {error && (
        <div className="field-error" id={errorId} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
